import { useState } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'

import CustomFieldValuesCollection from 'modules/custom_resources/store/collections/customFields/values'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import documents from 'modules/documents/store/collections/documents'
import type Employee from 'modules/employees/store/models/Employee'
import { leaveEmployee } from 'modules/timeoff/lib/leaveEmployees'
import medicalLeaveUpdates from 'modules/timeoff/store/collections/medicalLeaveUpdates'
import Leave from 'modules/timeoff/store/models/Leave'

import usePermissions from './usePermissions'

interface Props {
  leaveId: number
}

export type leaveConfirmationFields =
  | {
      leave_confirmation_id: string
      leave_confirmation_employee_id: string
      leave_confirmation_company_id: string
      remote_employee_id: string
      remote_request_id: string
      status: string
      is_initial_attest: boolean
      is_hospitalisation: boolean
      confirmed_start_date: Date
      confirmed_end_date: Date
      confirmed_diagnose_date: Date
      updated_at: Date
      created_at: Date
      error_messages: string[]
    }
  | Record<string, never>

const useLeaveData = ({ leaveId }: Props) => {
  const [leave, setLeave] = useState(new Leave(Leave.buildNullLeave(leaveId)))
  const [leaveConfirmationFields, setLeaveConfirmationFields] = useState({})
  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [isMedical, setIsMedical] = useState(false)
  const [medicalEffectiveOn, setMedicalEffectiveOn] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      // CUSTOM FIELDS

      const getValuesCollection = async (leave: Leave): Promise<CustomFieldValuesCollection> => {
        const isNoneInstance = leave.documents.length === 0
        const instanceId = isNoneInstance ? '0' : leave.documents[0]?.attributes.id

        const collection = new CustomFieldValuesCollection([], {
          model_ref: 'document',
          instance_id: instanceId,
        })

        if (isNoneInstance) return collection

        await collection.byUsageGroup([CustomField.UsageGroups.documents])

        return collection
      }

      await leave.fetch()
      setLeave(leave)
      setLeaveConfirmationFields(leave.attributes.leave_confirmation_fields)

      setEmployee(leaveEmployee(leave))

      const promiseArray = [
        documents.fetch({
          data: {
            leave_id: leave.attributes.id,
          },
        }),
      ]

      // MEDICAL LEAVE DATA
      if (!medicalLeaveUpdates.length) {
        promiseArray.push(
          medicalLeaveUpdates.fetch({
            data: {
              employee_id: leaveEmployee(leave).attributes.id,
            },
          })
        )
      }

      await Promise.all(promiseArray)

      const update = medicalLeaveUpdates.models.find(
        (u) => u.attributes.leave_id === leave.attributes.id && u.isAlta
      )

      setIsMedical(!!leave.attributes.medical_leave_id)
      setMedicalEffectiveOn(update?.attributes.effective_on)

      setLoading(false)
    }

    fetchData()
  }, [leaveId, leave.attributes.id])

  const permissions = usePermissions({
    leaveLoading: false,
    leave,
  })

  return {
    leave,
    loading,
    employee,
    leaveConfirmationDetails: leaveConfirmationFields,
    medicalLeave: {
      isMedical,
      medicalEffectiveOn,
    },
    permissions,
  }
}

export default useLeaveData
